/** @format */

import { Alert, Box, Grid } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { GetMessageReponse } from "../../../common/Logic/ServerResponse";
import StepperButtons from "../../../common/custom/StepperButtons";
import MessageResponse from "../../../common/feedback/messageResponse";
import { FeedbackDto } from "../../../common/models/requests/FeedbackDto";
import {
  selectApplicant,
  submitApplicationThunk,
} from "../ApplicantSlice";
import {
  ApplicationDetailsDto,
  DocumentsViewDto,
  UploadDocuments
} from "../Models";
import DocumentsList from "./DocumentsList";

interface Props {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  activeStep: number;
}
export default function DocumentUploads({ setActiveStep, activeStep }: Props) {
  const [feedback, setFeedback] = useState<FeedbackDto>({} as FeedbackDto);
  const [formValues, setFormValues] = useState<UploadDocuments>();
  const [application, setApplication] = useState<ApplicationDetailsDto>(
    {} as ApplicationDetailsDto
  );
  const [isSaveAndClose, setIsSaveAndClose] = useState<boolean>(false);
  const [retryDoc, setRetryDoc] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const applicantSelect = useAppSelector(selectApplicant);
  const [requiredDocuments, setRequiredDocuments] = useState<number>(1);

  const validationSchemaSubmitApplication = yup.object({
    documents: yup
      .array()
      .required("Please upload all required documents")
      .min(
        requiredDocuments,
        `*Please upload all ${requiredDocuments} required documents`
      ),
  });
  useEffect(() => {
    if (
      applicantSelect.loginState.accessToken &&
      applicantSelect.loginState.accessToken.application
    ) {
      const app = applicantSelect.loginState.accessToken.application;
      setApplication(app);
      setRequiredDocuments(app.documents.length);
    }
  }, [applicantSelect.loginState.accessToken]);

  const initialValues = {
    documents: [] as DocumentsViewDto[],
    document: {} as DocumentsViewDto,
  } as UploadDocuments;

  useEffect(() => {
    setRetryDoc(false);
    if (applicantSelect.applicantCreateState.status === "failed") {
      const feed = GetMessageReponse(
        applicantSelect.applicantCreateState.error,
        false,
        "fulfilled"
      );
      setFeedback(feed);
      setRetryDoc(true);
    } else {
      setRetryDoc(false);
    }
  }, [
    applicantSelect.applicantCreateState.status,
    applicantSelect.applicantCreateState.error,
  ]);

  const formikSubmitApplication: FormikProps<UploadDocuments> =
    useFormik<UploadDocuments>({
      initialValues: formValues || initialValues,
      validationSchema: validationSchemaSubmitApplication,
      onSubmit: (values: UploadDocuments) => {
        if (values.documents.filter((s) => s.link === "").length > 0) return;
        dispatch(submitApplicationThunk(values.documents)).then((resp: any) => {
          setTimeout(() => {
            if (
              (resp.type.includes("fulfilled") &&
                resp.payload?.validation.code === 201) ||
              (resp.type.includes("fulfilled") &&
                resp.payload?.validation.code === 200)
            ) {
              navigate("/applicant");
            }
          }, 1200);
        });
      },
    });

  // Keep submit Formik `documents` in sync with Redux after login, upload, or delete.
  useEffect(() => {
    const progress = applicantSelect.loginState.accessToken;
    const docs = progress?.application?.documents;
    if (!docs?.length) return;

    const data = { documents: docs } as UploadDocuments;
    setFormValues(data);
    void formikSubmitApplication.setFieldValue("documents", docs);
  }, [applicantSelect.loginState.accessToken?.application]);

  return (
    <>
      {application && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Alert variant="standard" color="info" severity="info">
              Please note that documents are uploaded automatically, once all
              documents are uploaded you can press submit application. All
              required files are also verified on the server when you submit.
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <MessageResponse feedback={feedback}/>
          </Grid>
        
          {applicantSelect.loginState?.accessToken && (
            <Grid container spacing={2}>
              <DocumentsList
                application={
                  applicantSelect.loginState?.accessToken.application
                }
              />
            </Grid>
          )}
          <Box sx={{ color: "#d32f2f" }}>
          {(application?.documents?.filter((s) => s.link === "").length ?? 0) >
            0 && "Please upload all required documents"}
          </Box>
          <Grid item xs={12} mt={3}>
            <StepperButtons
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              formik={formikSubmitApplication}
              status={applicantSelect.applicantCreateState.status}
              isDisabled={false}
              setSaveAndClose={setIsSaveAndClose}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
}
